// https://github.com/JedWatson/react-select

import React from 'react';
import PropTypes from 'prop-types';
import Select, { Creatable } from 'react-select';
import DataProvider from 'api/data-provider';
import Bem from 'bemHelper';
import findIndex from 'lodash/findIndex';
import size from 'lodash/size';
import trim from 'lodash/trim';
import Icon, { iconTypes } from 'components/icon';

import AbstractInput from '../abstractInput';
import InputLabel from '../input-label';
import ValidationState from '../validationState';

// import './input-select.scss';
// import 'react-select/dist/react-select.css'; // Оригинальные стили

import 'styles/base/containers/form/_input-select.scss';

/**
 * === InputSelect Component ===
 *
 * = Example =
 * <InputSelect
 *   url={url}
 *   dataBind={dataBind}
 *   queryWord="tag"
 *   minLength={2}
 * />
 */

const bemClasses = new Bem('input');

export default class InputSelect extends AbstractInput {
  static propTypes = {
    ...AbstractInput.props,
    disableValidationState: PropTypes.bool,
    noError: PropTypes.bool,
    hasLabel: PropTypes.bool,

    // DataProvider url prop
    url: PropTypes.string,

    // DataProvider query prop name
    queryWord: PropTypes.string,

    // Input name
    name: PropTypes.string,

    // Min string length entered into input for DataProvider request
    minLength: PropTypes.number,

    // Allow to create new elements inside component
    withCreation: PropTypes.bool
  };

  static defaultProps = {
    hasLabel: true,
    minLength: 3
  };

  constructor(props, ctx) {
    super(props, ctx);
    this.handleChange = this.handleChange.bind(this);
    this.handleOptionCreate = this.handleOptionCreate.bind(this);
    this.isOptionUnique = this.isOptionUnique.bind(this);
    this.onHandleFocus = this.onHandleFocus.bind(this);
    this.onHandleBlur = this.onHandleBlur.bind(this);
    this.optionRenderer = this.optionRenderer.bind(this);
    this.clearRenderer = this.clearRenderer.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);

    this.state = {
      searchParams: '',
      focused: false
    };
    this.textCreator = 'Создать тэг';
  }

  onHandleFocus() {
    if (!this.state.focused) {
      this.setState({ focused: true });
    }
  }

  onHandleBlur() {
    if (this.state.focused) {
      this.setState({ focused: false });
    }
  }

  handleChange(valueArray) {
    // this.setState({ searchParams: '' });
    this.props.onChange(valueArray);
  }

  optionRenderer(option) { // eslint-disable-line
    return (
      <div {...bemClasses('btnOption')}>
        <span {...bemClasses('btnOptionContent')}>
          <span {...bemClasses('text')}>{option.label}</span>
        </span>
      </div>
    );
  }

  handleOptionCreate({ label }) {
    const trimmed = label ? trim(label.replace(this.textCreator, '')) : null;
    if (trimmed) {
      const lowerLabel = trimmed.toLowerCase();
      return { label: lowerLabel, id: lowerLabel, value: lowerLabel, new: true };
    }
  }

  handleInputChange(value) {
    const string = value ? value.replace(/\s\s+/g, ' ') : '';
    if (string.length >= this.props.minLength) {
      this.setState({ searchParams: string });
    } else if (!string || string.length < this.state.searchParams.length) {
      this.setState({ searchParams: '' });
    }
    return string;
  }

  isOptionUnique({ option, options, labelKey, valueKey }) {
    const index = findIndex(options, opt => (
      opt[labelKey].toLowerCase() === option[labelKey].toLowerCase() &&
      opt[valueKey].toLowerCase() === option[valueKey].toLowerCase()
    ));
    return index === -1;
  }

  clearRenderer() {
    return <Icon {...bemClasses('icon', 'clear')} type={iconTypes.clear} />;
  }

  render() {
    const {
      className, url, name, value, queryWord, schema = {}, withCreation,
      hasLabel, validationState, noError
    } = this.props;
    const { searchParams, focused } = this.state;
    const empty = this.props.disableValidationState;
    const mods = { select: true, empty };
    const filled = focused || (value !== null && !!size(value));

    if (!withCreation) {
      return (
        <div {...bemClasses(null, mods, className)}>
          <div {...bemClasses('inputWrapper')}>
            <div {...bemClasses('inputContainer')}>
              <DataProvider
                url={url}
                injectPropName="options"
              >
                <Select
                  {...bemClasses('input', { filled })}
                  name={name && name}
                  value={value || []}
                  placeholder={false}
                  backspaceToRemoveMessage=""
                  onChange={this.handleChange}
                  onFocus={this.onHandleFocus}
                  onBlur={this.onHandleBlur}
                  optionRenderer={this.optionRenderer}
                  clearRenderer={this.clearRenderer}
                  clearAllText="Очистить"
                  noResultsText={
                    <div {...bemClasses('btnOption', 'noResultsText')}>
                      <span {...bemClasses('btnOptionContent')}>
                        <span {...bemClasses('text')}>Совпадений не найдено</span>
                      </span>
                    </div>
                  }
                  multi
                />
              </DataProvider>
              {hasLabel &&
                <InputLabel
                  {...bemClasses('inputLabel')}
                  schema={schema}
                  value={filled}
                  validationState={validationState}
                  inside
                />
              }
              <span {...bemClasses('bottomLine')} />
            </div>
            {!noError &&
              <div {...bemClasses('errorWrapper')}>
                {!this.props.disableValidationState &&
                  <ValidationState
                    validationState={this.props.validationState}
                  />
                }
              </div>
            }
          </div>
        </div>
      );
    }

    return (
      <div {...bemClasses(null, mods, className)}>
        <div {...bemClasses('inputWrapper')}>
          <div {...bemClasses('inputContainer')}>
            <DataProvider
              url={searchParams && url}
              query={{ [queryWord]: searchParams }}
              queryJson
              injectPropName="options"
            >
              <Creatable
                {...bemClasses('input', { filled })}
                name={name && name}
                value={value || []}
                placeholder={false}
                closeOnSelect={false}
                backspaceToRemoveMessage=""
                onChange={this.handleChange}
                onInputChange={this.handleInputChange}
                onFocus={this.onHandleFocus}
                onBlur={this.onHandleBlur}
                newOptionCreator={this.handleOptionCreate}
                isOptionUnique={this.isOptionUnique}
                optionRenderer={this.optionRenderer}
                clearRenderer={this.clearRenderer}
                isValidNewOption={({ label }) => (!!trim(label) && trim(label).length > 1)}
                clearAllText="Очистить"
                promptTextCreator={label => `${this.textCreator} ${label}`}
                noResultsText={
                  <div {...bemClasses('btnOption', 'noResultsText')}>
                    <span {...bemClasses('btnOptionContent')}>
                      <span {...bemClasses('text')}>Совпадений не найдено</span>
                    </span>
                  </div>
                }
                multi
              />
            </DataProvider>
            {hasLabel &&
              <InputLabel
                {...bemClasses('inputLabel')}
                schema={schema}
                value={filled}
                validationState={validationState}
                inside
              />
            }
            <span {...bemClasses('bottomLine')} />
          </div>
          {!noError &&
            <div {...bemClasses('errorWrapper')}>
              {!this.props.disableValidationState &&
                <ValidationState
                  validationState={this.props.validationState}
                />
              }
            </div>
          }
        </div>
      </div>
    );
  }
}
